"use client";

import React, { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { useChatContext } from '@/contexts/ChatContext';
import { CenteredChatInput } from './CenteredChatInput';
import { ChatMobile } from './ChatMobile';
import { ChatDesktop } from './ChatDesktop';
import { useIsMobile } from '@/hooks/use-mobile';
import { DesktopSideNavigationIcon, SideNavigationIcon } from '../icons';
function ChatComponent() {
  const { activeChat, activeChatId, chatSessions, isLoading, isSidebarOpen, openSidebar, closeSidebar, RateLimitDialogComponent, userId } = useChatContext();
  const [showCenteredInput, setShowCenteredInput] = useState(true);
  const [isDesktopSidebarDismissed, setIsDesktopSidebarDismissed] = useState(false);
  const [hasCachedConversations, setHasCachedConversations] = useState(false);
  const isMobile = useIsMobile();

  const activeChatHasMessages = Boolean(activeChat && activeChat.messages.length > 0);
  const hasChats = chatSessions.length > 0;
  const hasAnyExistingChats = hasChats || (hasCachedConversations && !isMobile);
  const shouldShowCenteredInput = showCenteredInput
    && (!activeChat || !activeChatHasMessages)
    && (isMobile || (!isLoading && !hasAnyExistingChats));

  const shouldSkipCenteredView = !isMobile && (hasAnyExistingChats || (isLoading && hasCachedConversations));

  useEffect(() => {
    if (typeof window === 'undefined' || !userId || isMobile) {
      setHasCachedConversations(false);
      return;
    }

    try {
      const cacheKey = `dify-conversations-${userId}`;
      const cached = localStorage.getItem(cacheKey);
      setHasCachedConversations(Boolean(cached && cached !== '[]'));
    } catch {
      setHasCachedConversations(false);
    }
  }, [userId, isMobile]);

  useEffect(() => {
    if (!isMobile && hasAnyExistingChats && showCenteredInput) {
      setShowCenteredInput(false);
    }
  }, [isMobile, hasAnyExistingChats, showCenteredInput]);

  // Ensure we exit the centered view when the sidebar opens from elsewhere
  useEffect(() => {
    if (isSidebarOpen && shouldShowCenteredInput) {
      setShowCenteredInput(false);
    }
  }, [isSidebarOpen, shouldShowCenteredInput]);

  // Whenever a chat becomes active (e.g., via "Open last conversation"),
  // exit the centered input view so the conversation becomes visible.
  useEffect(() => {
    if (activeChatId && showCenteredInput) {
      setShowCenteredInput(false);
    }
  }, [activeChatId, showCenteredInput]);

  useEffect(() => {
    if (isMobile) {
      if (isDesktopSidebarDismissed) {
        setIsDesktopSidebarDismissed(false);
      }
      return;
    }

    const hasChatsDesktop = chatSessions.length > 0;

    if (!isLoading && hasChatsDesktop && !isSidebarOpen && !isDesktopSidebarDismissed) {
      openSidebar();
    }
  }, [isMobile, isSidebarOpen, openSidebar, isDesktopSidebarDismissed, chatSessions.length, isLoading]);

  const handleMessageSent = () => {
    setShowCenteredInput(false);
  };

  const handleOpenSidebar = () => {
    if (shouldShowCenteredInput) {
      setShowCenteredInput(false);
    }
    if (!isMobile) {
      setIsDesktopSidebarDismissed(false);
    }
    openSidebar();
  };

  const handleCloseSidebar = () => {
    if (!isMobile) {
      setIsDesktopSidebarDismissed(true);
    }
    closeSidebar();
  };

  const showOpenToggle = !isSidebarOpen && chatSessions.length > 0;

  return (
    <div className="relative h-full min-h-0 flex flex-col">
      {showOpenToggle && !isMobile && (
        <button
          onClick={handleOpenSidebar}
          className="absolute top-4 left-4 z-30 text-neutral-400 hover:text-white transition-colors h-7 w-7 flex items-center justify-center"
          title="Open sidebar"
        >
          {isMobile ? <SideNavigationIcon /> : <DesktopSideNavigationIcon />}
        </button>
      )}

      {(shouldShowCenteredInput && !shouldSkipCenteredView) ? (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-full max-w-4xl mx-auto">
            <CenteredChatInput onMessageSent={handleMessageSent} />
          </div>
        </div>
      ) : isMobile ? (
        <ChatMobile isSidebarOpen={isSidebarOpen} onRequestCloseSidebar={handleCloseSidebar} />
      ) : (
        <ChatDesktop onRequestCloseSidebar={handleCloseSidebar} />
      )}
      
      {/* Rate limit dialog */}
      {RateLimitDialogComponent}
    </div>
  );
}

export default ChatComponent;
